import styled from '@emotion/styled';
import { Button, Text, tokens } from '@hotjar/hotsauce';
import { capitalize } from 'lodash-es';
import type { Site } from '@hotjar/site';
import { useTranslation } from '@hotjar/translations';
import { OVERVIEW } from '@hotjar/webapp/constants/routes';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useGtmOAuthUrl } from '../../../SetupWizard/hooks/useGtmOAuthUrl';
import { CMS, STEPS } from '../../../SetupWizard/steps';
import { IntegrationsIcon } from '../../../SetupWizard/steps/IntegrationGuides';

export const HeroWrapper = styled.div<{ isTCIPage?: boolean }>`
  display: flex;
  flex-direction: row;
  position: relative;
  background-color: ${({ isTCIPage }) =>
    isTCIPage ? tokens.color.ui_background : tokens.color.cobalt_100};
  padding: ${tokens.spacer.x3};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};
`;

export const HeroCopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: ${tokens.spacer.x2};
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x3};
`;

const CmsButton = styled(Button)`
  width: fit-content;
`;

const StyledIntegrationsIcon = styled(IntegrationsIcon)`
  align-self: flex-start;
`;

export const CmsCard = ({
  site,
  platform,
  isPlatformGtm,
}: {
  site: Site;
  platform?: CMS;
  isPlatformGtm: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const track = useTrack();
  const gtmOAuthURL = useGtmOAuthUrl();
  const isGtm = isPlatformGtm || platform === CMS.GTM;

  const onGtmStartSetupClick = () => {
    track.click('manual installation', 'GTM Setup Installation', {});
    window.location.assign(gtmOAuthURL);
  };
  const onCmsStartSetupClick = () => {
    track.click('manual installation', 'CMS Setup Installation', {});
  };

  const onOtherInstallMethodsClick = () => {
    track.click('manual installation', 'Other Install Methods', {});
  };

  return (
    <HeroWrapper data-testid="hero-section">
      {platform ? <StyledIntegrationsIcon icon={platform} /> : null}
      <HeroCopyWrapper>
        <Text
          style={{ marginBottom: tokens.spacer.x1 }}
          textType="header4"
          textColor="text_default"
          as="h2"
        >
          {isGtm
            ? t('activation:setup-wizard.main-page.hero.title-gtm')
            : t('activation:setup-wizard.main-page.hero.title-cms', { cms: capitalize(platform) })}
        </Text>
        <Text
          style={{ marginBottom: tokens.spacer.x3 }}
          textType="body2"
          textColor="text_default"
          as="p"
        >
          {isGtm
            ? t('activation:setup-wizard.main-page.hero.description-gtm')
            : t('activation:setup-wizard.main-page.hero.description-cms', {
                cms: capitalize(platform),
              })}
        </Text>
        {isGtm ? (
          <ButtonWrapper>
            <Button
              onClick={onGtmStartSetupClick}
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('activation:space-overview.gtm-banner.cta')}
            </Button>
            <Button
              as="a"
              kind="ghost"
              href={`${getRoute(OVERVIEW.SETUP.path, {
                siteId: site.id,
              })}?step=${STEPS.INSTALLATION_METHOD}`}
              data-testid="other-install-methods-cta"
              onClick={onOtherInstallMethodsClick}
            >
              {t('activation:space-overview.gtm-banner.secondary-cta')}
            </Button>
          </ButtonWrapper>
        ) : (
          <CmsButton
            as="a"
            onClick={onCmsStartSetupClick}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            href={`${getRoute(OVERVIEW.SETUP.path, {
              siteId: site.id,
            })}?step=${STEPS.CMS}`}
            data-testid="cms-setup-cta"
          >
            {t('activation:space-overview.cms-banner.cta')}
          </CmsButton>
        )}
      </HeroCopyWrapper>
    </HeroWrapper>
  );
};
